// frontend/src/components/surveys/FeedbackSurveys.js

/**
 * Feedback Surveys Component
 * Handles both token-based survey access (from email links) and authenticated access
 * Supports sequential display of multiple surveys (service + system)
 */

import React, { useCallback, useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from '../../axiosConfig';
import { useTheme } from '../../contexts/ThemeContext';
import ThemedCard from '../themed/ThemedCard';
import ThemedButton from '../themed/ThemedButton';
import SurveyDisplay from '../SurveyDisplay';
import NPSFollowUp from './NPSFollowUp';
import logger from '../../utils/logger';

const getTokenFlowErrorDetails = (error, token) => ({
    code: error?.code,
    message: error?.message,
    status: error?.response?.status,
    surveyTokenPresent: !!token
});

const FeedbackSurveys = () => {
    const { t } = useTranslation();
    const { background, text } = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // State
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [surveys, setSurveys] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [allCompleted, setAllCompleted] = useState(false);
    const [npsFollowUp, setNpsFollowUp] = useState(null);

    // Check if accessing via token
    const token = searchParams.get('token');
    const isTokenMode = !!token;

    /**
     * Load surveys from token (email link access)
     */
    const loadSurveysFromToken = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `/api/surveys/respond/${token}`,
                { skipAuthSessionHandling: true }
            );

            if (response.data.success) {
                setSurveys(response.data.data.surveys || []);

                if (response.data.data.surveys.length === 0) {
                    setAllCompleted(true);
                }
            } else {
                setError(response.data.error || t('survey.feedback.loadingError'));
            }
        } catch (error) {
            logger.error('Failed to load surveys from token', getTokenFlowErrorDetails(error, token));
            setError(error.response?.data?.error || t('survey.feedback.loadingError'));
        } finally {
            setLoading(false);
        }
    }, [t, token]);

    /**
     * Load surveys for authenticated users (in-app access)
     */
    const loadAuthenticatedSurveys = useCallback(async () => {
        try {
            setLoading(true);
            // TODO: Implement authenticated survey loading if needed
            // For now, authenticated users use the existing VoluntarySurvey component
            setError(t('pleaseUseEmailLink'));
        } catch (error) {
            logger.error('Failed to load authenticated surveys', {
                code: error?.code,
                message: error?.message,
                status: error?.response?.status
            });
            setError(t('survey.feedback.loadingError'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        if (isTokenMode) {
            loadSurveysFromToken();
        } else {
            loadAuthenticatedSurveys();
        }
    }, [isTokenMode, loadAuthenticatedSurveys, loadSurveysFromToken]);

    /**
     * Handle survey completion
     */
    const handleSurveyComplete = async (responses) => {
        try {
            const currentSurvey = surveys[currentIndex];

            // Submit survey responses
            const response = await axios.post(
                `/api/surveys/respond/${token}/${currentSurvey.type}`,
                { responses },
                { skipAuthSessionHandling: true }
            );

            if (response.data.success) {
                toast.success(t('surveySubmittedSuccessfully'));

                // Check for NPS follow-up (system survey only)
                if (currentSurvey.type === 'system' && response.data.npsData) {
                    setNpsFollowUp(response.data.npsData);
                    return false; // Show NPS follow-up instead of the generic thank-you flow
                }

                // Move to next survey or mark as complete
                if (currentIndex < surveys.length - 1) {
                    setCurrentIndex(currentIndex + 1);
                } else {
                    setAllCompleted(true);
                }
            } else {
                toast.error(t('failedToSubmitSurvey'));
                return false;
            }
        } catch (error) {
            logger.error('Failed to submit survey', getTokenFlowErrorDetails(error, token));
            toast.error(t('failedToSubmitSurvey'));
            return false;
        }

        return true;
    };

    /**
     * Handle NPS follow-up actions
     */
    const handleNPSAction = async (action) => {
        try {
            if (action === 'invite') {
                // Track invitation action
                await axios.post(
                    `/api/surveys/nps-follow-up/${npsFollowUp.id}`,
                    {
                        action: 'invited_users',
                        invitedCount: 0, // Will be set by invite page
                        token: token
                    },
                    { skipAuthSessionHandling: true }
                );

                // Navigate to invite page
                navigate('/invite-users');
            } else if (action === 'dismiss') {
                // Track dismissal
                await axios.post(
                    `/api/surveys/nps-follow-up/${npsFollowUp.id}`,
                    {
                        action: 'dismissed',
                        token: token
                    },
                    { skipAuthSessionHandling: true }
                );

                // Mark as fully completed
                setNpsFollowUp(null);
                setAllCompleted(true);
            }
        } catch (error) {
            logger.error('Failed to record NPS action', getTokenFlowErrorDetails(error, token));
            // Continue anyway
            setNpsFollowUp(null);
            setAllCompleted(true);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className={`min-h-screen ${background.page} flex items-center justify-center`}>
                <ThemedCard className="p-8">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                        <p className={text.secondary}>{t('survey.feedback.loadingMessage')}</p>
                    </div>
                </ThemedCard>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className={`min-h-screen ${background.page} flex items-center justify-center p-4`}>
                <ThemedCard className="p-8 max-w-md">
                    <div className="text-center">
                        <p className="text-red-500 mb-4">{t('survey.feedback.loadingError')}</p>
                        <p className={text.secondary}>{error}</p>
                    </div>
                </ThemedCard>
            </div>
        );
    }

    // Completion state
    if (allCompleted) {
        return (
            <div className={`min-h-screen ${background.page} flex items-center justify-center p-4`}>
                <ThemedCard className="p-8 max-w-md text-center">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold mb-4">
                        {t('survey.feedback.completionSuccess')}
                    </h2>
                    {!isTokenMode && (
                        <ThemedButton
                            onClick={() => navigate('/')}
                            className="mt-4"
                        >
                            {t('survey.feedback.returnToDashboard')}
                        </ThemedButton>
                    )}
                </ThemedCard>
            </div>
        );
    }

    // NPS follow-up state
    if (npsFollowUp) {
        return (
            <NPSFollowUp
                isPromoter={npsFollowUp.isPromoter}
                message={npsFollowUp.message}
                onInvite={() => handleNPSAction('invite')}
                onDismiss={() => handleNPSAction('dismiss')}
            />
        );
    }

    // Survey display
    const currentSurvey = surveys[currentIndex];

    return (
        <div className={`min-h-screen ${background.page} py-8 px-4`}>
            <div className="max-w-3xl mx-auto">
                {/* Progress indicator */}
                {surveys.length > 1 && (
                    <div className="mb-6">
                        <div className="flex justify-between items-center mb-2">
                            <span className={`text-sm ${text.secondary}`}>
                                {t('survey.feedback.progressLabel', {
                                    current: currentIndex + 1,
                                    total: surveys.length
                                })}
                            </span>
                            <span className={`text-sm font-medium ${text.primary}`}>
                                {t('survey.feedback.percentComplete', {
                                    percent: Math.round(((currentIndex + 1) / surveys.length) * 100)
                                })}
                            </span>
                        </div>
                        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-blue-500 transition-all duration-300"
                                style={{ width: `${((currentIndex + 1) / surveys.length) * 100}%` }}
                            />
                        </div>
                    </div>
                )}

                {/* Survey title */}
                <ThemedCard className="mb-6 p-6">
                    <h1 className="text-2xl font-bold mb-2">
                        {currentSurvey.type === 'service'
                            ? t('survey.feedback.serviceTitle')
                            : t('survey.feedback.systemTitle')}
                    </h1>
                    <p className={text.secondary}>
                        {currentSurvey.survey.description ||
                         (currentSurvey.type === 'service'
                             ? t('survey.feedback.serviceDescription')
                             : t('survey.feedback.systemDescription'))}
                    </p>
                </ThemedCard>

                {/* Survey questions */}
                <SurveyDisplay
                    key={`${currentSurvey.type}-${currentSurvey.survey.id}`}
                    survey={currentSurvey.survey}
                    submitResponses={handleSurveyComplete}
                    onClose={isTokenMode ? undefined : () => navigate(-1)}
                    displayDelay={0}
                />
            </div>
        </div>
    );
};

export default FeedbackSurveys;
